import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  restoreQuantityPublic(modalContent) {
    this.#restoreQuantity(modalContent);
  }


  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);

        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      await okeWidgetApi.initAllWidgets()
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const fetchDocument = async (targetUrl) => {
        const response = await fetch(targetUrl, {
          signal: this.#abortController.signal,
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
        }

        const responseText = await response.text();
        return new DOMParser().parseFromString(responseText, 'text/html');
      };

      const sectionUrl = new URL(productPageUrl);
      // Ensure we render the product section markup (media + details) directly
      sectionUrl.searchParams.set('section_id', 'template--19896197283978__main');

      let html = null;
      try {
        html = await fetchDocument(sectionUrl.toString());
      } catch (error) {
        // If the section render fails (e.g., incorrect section id), fall back to full page HTML
        if (error.name !== 'AbortError') {
          html = await fetchDocument(productPageUrl);
        } else {
          throw error;
        }
      }

      // Safari occasionally returns empty section HTML; retry with full page as a fallback
      if (!html.querySelector('[data-product-grid-content]')) {
        html = await fetchDocument(productPageUrl);
      }

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    this.#restoreQuantity(modalContent);
  }

  #restoreQuantity(modalContent) {
  const selectedInput = modalContent.querySelector('.quanity_input:checked');
  const qtyInput = selectedInput?.value || 1;
  const percentOff = selectedInput ? Number(selectedInput.dataset.percentageOff) || 0 : 0;
  const currency = modalContent.querySelector('product-qty')?.dataset.storeCurrency || '';
  const priceEl = modalContent.querySelector(
    'add-to-cart-component .add-to-cart-text .price'
  );

  const priceText = modalContent?.querySelector(
      'add-to-cart-component button'
    )?.dataset.productPrice;

  const variantPrice = Number(priceText.replace(/[^0-9.]/g, ''));
  const originalTotal = variantPrice * qtyInput;
  const discountedTotal = originalTotal * (1 - percentOff / 100);
  priceEl.textContent = `${currency}${discountedTotal.toFixed(2)}`;

  const priceContainer = priceEl.closest('[ref="priceContainer"]') || priceEl.parentElement;
  let compareEl = priceContainer.querySelector('.compare-at-price');
  if (percentOff > 0) {
    if (!compareEl) {
      compareEl = document.createElement('span');
      compareEl.className = 'compare-at-price';
      compareEl.style.marginRight = '5px';
      priceContainer.insertBefore(compareEl, priceContainer.firstChild);
    }
    compareEl.textContent = `${currency}${originalTotal.toFixed(2)}`;
    compareEl.style.display = '';
  } else if (compareEl) {
    compareEl.style.display = 'none';
  }
  
}


  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) {
      this.#ensureModalDefaultVariant(modalContent);
      return;
    }

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    this.#ensureModalDefaultVariant(modalContent);
    this.#restoreQuantity(modalContent);
  }

  /**
   * Ensures the modal has a default variant selected.
   * @param {Element} modalContent - The modal content element
   */
  #ensureModalDefaultVariant(modalContent) {
    const checkedInput = modalContent.querySelector('input[type="radio"][data-variant-id]:checked');
    if (checkedInput) return;

    let defaultVariantId = null;
    const variantDataScript = modalContent.querySelector('variant-picker script[type="application/json"]');
    if (variantDataScript?.textContent) {
      try {
        const variantData = JSON.parse(variantDataScript.textContent);
        if (variantData?.id) {
          defaultVariantId = String(variantData.id);
        }
      } catch (error) {
        console.warn('Quick add: unable to parse default variant data.', error);
      }
    }

    /** @type {HTMLInputElement | null} */
    const defaultInput = defaultVariantId
      ? modalContent.querySelector(`input[type="radio"][data-variant-id="${defaultVariantId}"]`)
      : modalContent.querySelector('input[type="radio"][data-variant-id]:not([disabled])');

    if (defaultInput && !defaultInput.checked) {
      defaultInput.checked = true;
      defaultInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantChange);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #handleVariantChange = (event) => {
    const modalContent = document.querySelector('#quick-add-modal-content');
    if (!modalContent) return;

    const quickAdd = document.querySelector('quick-add-component');
    if (!quickAdd) return;

    quickAdd.restoreQuantityPublic(modalContent);
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

document.addEventListener('change', (e) => {
  if (!e.target.classList.contains('quanity_input')) return;

  // Only run for quick add modal
  const modalContent = e.target.closest('#quick-add-modal-content');
  if (!modalContent) return;

  const quickAdd = document.querySelector('quick-add-component');
  if (!quickAdd) return;

  // Recalculate price = variant price × selected qty
  quickAdd.restoreQuantityPublic(modalContent);
});

